import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, FormControlLabel, Grid, Switch, Typography, useTheme } from "@mui/material";
import { faBuilding, faStairs } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import FontAwesomeSvgIcon from "../../ui/FontAwesomeSvgIcon";
import SensorCard from "./SensorCard";
import { Building } from "./SensorOverview";

const SensorGrid: React.FC<{
  buildings: Building[];
  allFloors: string[];
  selectedSensors?: string[];
  targetTemperature?: number | null;
  onSensorSelect?: (sensorId: string) => void;
}> = ({ buildings, allFloors, selectedSensors = [], targetTemperature = null, onSensorSelect }) => {
  const ROW_HEIGHT = 80;
  const { t } = useTranslation(["propai", "_messages", "propai_properties"]);
  const theme = useTheme();
  const [loftMode, setLoftMode] = useState(false);

  // Detect if any apartment starts with "L" (loft)
  const hasLoftApartments = useMemo(() => {
    return buildings.some((building) =>
      Array.from(building.sensors.values()).some(({ sensor }) => sensor.apartment?.startsWith("L"))
    );
  }, [buildings]);

  // Automatically enable loft mode when loft apartments are detected
  useEffect(() => {
    setLoftMode(hasLoftApartments);
  }, [hasLoftApartments]);

  const sortedBuildings = useMemo(() => {
    return buildings.sort((a, b) => a.buildingId.localeCompare(b.buildingId));
  }, [buildings]);

  // Separate floors into regular and loft floors
  const { regularFloors, loftFloors } = useMemo(() => {
    if (!loftMode) {
      return { regularFloors: allFloors, loftFloors: [] };
    }

    const regular: string[] = [];
    const loft: string[] = [];

    allFloors.forEach((floor) => {
      // Check if this floor has any loft apartments
      const hasLoftOnFloor = buildings.some((building) =>
        Array.from(building.sensors.values()).some(
          ({ sensor }) => sensor.floor === floor && sensor.apartment?.startsWith("L")
        )
      );

      // Check if this floor has any regular apartments
      const hasRegularOnFloor = buildings.some((building) =>
        Array.from(building.sensors.values()).some(
          ({ sensor }) => sensor.floor === floor && !sensor.apartment?.startsWith("L")
        )
      );

      // A floor can be in both arrays if it has both types
      if (hasLoftOnFloor) {
        loft.push(floor);
      }
      if (hasRegularOnFloor) {
        regular.push(floor);
      }
    });

    return { regularFloors: regular, loftFloors: loft };
  }, [loftMode, allFloors, buildings]);
  // Helper function to render floors
  const renderFloorRows = (floors: string[], isLoftSection: boolean) => {
    return floors.map((floor) => (
      <Box
        key={`${isLoftSection ? "loft" : "regular"}-${floor}`}
        height={ROW_HEIGHT}
        display="flex"
        alignItems="center"
        sx={{ borderBottom: `2px solid ${theme.palette.divider}` }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <FontAwesomeIcon icon={faStairs} color={theme.palette.text.primary} />
          <Typography variant="labelLarge" color="text.primary">
            {isLoftSection ? `L${floor}` : floor}
          </Typography>
        </Box>
      </Box>
    ));
  };

  // Helper function to render building sensors for floors
  const renderBuildingSensors = (building: Building, floors: string[], isLoftSection: boolean) => {
    return floors.map((floor) => {
      const sensorForFloor = Array.from(building.sensors.values())
        .filter(({ sensor }) => {
          // Only filter by loft mode when it's enabled
          if (loftMode) {
            const isLoft = sensor.apartment?.startsWith("L");
            if (isLoftSection !== isLoft) return false;
          }

          // If floor is 'N/A', include sensors with missing or 'N/A' floors
          if (floor === "N/A") {
            return !sensor.floor || sensor.floor === "N/A";
          }
          return sensor.floor === floor;
        })
        .sort((a, b) => a.sensor.apartment?.localeCompare(b.sensor.apartment || "") || 0);

      return (
        <Box
          key={`${isLoftSection ? "loft" : "regular"}-${floor}`}
          sx={{
            height: ROW_HEIGHT,
          }}
        >
          {sensorForFloor.length > 0 ? (
            <Box display="flex" flexWrap="wrap">
              {sensorForFloor.map(({ sensor, lastReading }) => (
                <SensorCard
                  key={sensor.uid}
                  sensor={sensor}
                  lastReading={lastReading}
                  isSelected={selectedSensors.includes(sensor.uid)}
                  onSelect={onSensorSelect}
                  targetTemperature={targetTemperature}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                width: "80px",
                height: ROW_HEIGHT,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          )}
        </Box>
      );
    });
  };

  // Helper function to render loft separator
  const renderLoftSeparator = () => (
    <Box
      height={ROW_HEIGHT / 2}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        borderBottom: `3px solid ${theme.palette.text.secondary}`,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Typography
        variant="labelLarge"
        color="text.primary"
        textTransform={loftMode ? "uppercase" : "none"}
      >
        {t("loft")}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      {hasLoftApartments && (
        <Box display="flex" justifyContent="space-between" mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={loftMode}
                onChange={(e) => setLoftMode(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="labelLarge" color="text.primary">
                {t("separate_loft_apartments")}
              </Typography>
            }
          />
        </Box>
      )}
      <Grid container>
        {/* Floor numbers column */}
        <Grid item sx={{ minWidth: "80px", borderRight: `1px solid ${theme.palette.divider}` }}>
          <Box>
            {/* Loft floors */}
            {loftMode && renderFloorRows(loftFloors, true)}
            {/* Loft separator */}
            {loftMode && loftFloors.length > 0 && renderLoftSeparator()}
            {/* Regular floors */}
            {renderFloorRows(regularFloors, false)}
          </Box>
        </Grid>

        {/* Building columns */}
        {sortedBuildings.map((building, index) => (
          <Grid item key={building.buildingId}>
            <Box>
              {/* Loft floor sensors */}
              {loftMode && renderBuildingSensors(building, loftFloors, true)}

              {/* Loft separator */}
              {loftMode && loftFloors.length > 0 && (
                <Box
                  height={ROW_HEIGHT / 2}
                  sx={{
                    borderBottom: `3px solid ${theme.palette.text.secondary}`,
                    backgroundColor: theme.palette.background.default,
                  }}
                />
              )}

              {/* Regular floor sensors */}
              {renderBuildingSensors(building, regularFloors, false)}
              {/* Building footer */}
              <Box
                height={ROW_HEIGHT}
                display="flex"
                alignItems="center"
                justifyContent="center"
                minWidth="80px"
                sx={{
                  borderTop: `1px solid ${theme.palette.divider}`,
                  borderLeft: `1px solid ${theme.palette.divider}`,
                  borderRight: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} p={0.5}>
                  <FontAwesomeSvgIcon icon={faBuilding} />
                  <Typography
                    variant="labelLarge"
                    sx={{
                      whiteSpace: "wrap",
                      overflow: "hidden",
                      textOverflow: "wrap",
                      color: "text.primary",
                    }}
                    maxWidth="150px"
                  >
                    {building.buildingId}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default SensorGrid;
